import { Injectable } from '@angular/core';

import { isBomSubLine } from 'app/utils/is-bom-sub-line';
import {
  AppProductLine as ProductLine,
  AppCustomLine as CustomLine,
  LineCustomPropertyKeyType, SnkLineId
} from 'graphql/models/extended';
import { CustomPropertyInputArbonia, LineInputType } from 'graphql/models/generated';
import { LineSubtypePropVal } from 'models/position.model';

@Injectable({
  providedIn: 'root'
})
export class PositionCustomPropertiesService {
  extractPositionNumbers(
    positions: Array<ProductLine | CustomLine>,
    lineUpdates?: LineInputType[]
  ): string[] {
    let update: LineInputType | undefined;
    let prop: CustomPropertyInputArbonia | undefined;

    if (Array.isArray(lineUpdates) && lineUpdates.length > 0) {
      return positions
        .filter((pos) => !isBomSubLine(pos))
        .map((pos) => {
          update = lineUpdates.find((upd) => upd.id === pos.id);
          if (update && update.properties) {
            prop = update.properties.find((p) => p.key === LineCustomPropertyKeyType.ArticleID);
            if (prop && prop.value) {
              return prop.value;
            }
          }

          return this.getPositionNumber(pos);
        })
        .flat()
        .filter(num => num);
    }

    return positions
      .filter((pos) => !isBomSubLine(pos))
      .map((pos) => this.getPositionNumber(pos)).flat().filter(num => num);
  }

  getPositionNumber(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.ArticleID);
  }

  getDescriptiveText(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.DescriptiveText);
  }

  getOptional(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.Optional);
  }

  getOptionalOrAlternative(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.OptionalOrAlternative);
  }

  getLineId(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.LineID);
  }

  getProductID(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.ProductID);
  }

  getConfigurationSnapshot(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.ConfigurationSnapshot);
  }

  isDetachedLine(pos: ProductLine | CustomLine): boolean {
    return Boolean(this.getLineId(pos) === '');
  }

  isParentLine(pos: ProductLine | CustomLine): boolean {
    return Boolean(this.getLineId(pos) === SnkLineId.Parent);
  }

  isChildLine(pos: ProductLine | CustomLine): boolean {
    return !this.isParentLine(pos) && !this.isDetachedLine(pos);
  }

  isOptionalLine(pos: ProductLine | CustomLine): boolean {
    return Boolean(this.getOptional(pos) !== '');
  }

  isOptionalOrAlternativeLine(pos: ProductLine | CustomLine): boolean {
    return Boolean(this.getOptionalOrAlternative(pos) === 'true');
  }

  getLineSubtype(pos: ProductLine | CustomLine): LineSubtypePropVal {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.LineSubtype) as LineSubtypePropVal;
  }

  getLineKurztext(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.CustomPositionskurztext);
  }

  getFreeArticleDescription(pos: ProductLine | CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.FreieArtikelDescription);
  }

  getLineArticleDescription(pos: CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.MeineArtikelDescription);
  }

  getLineArticleConfiguration(pos: CustomLine): string {
    return this.getCustomProperty(pos, LineCustomPropertyKeyType.CustomConfigurationSnapshot);
  }

  private getCustomProperty(pos: ProductLine | CustomLine, key: LineCustomPropertyKeyType): string {
    const prop = ((pos && pos.customProperties) || []).find((p) => p.key === key);

    return (prop && prop.stringValue) || '';
  }
}
